import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, Button, Checkbox, FormControlLabel, Link } from "@mui/material";
import { WarningAmber } from "@mui/icons-material";
import axios from "axios";
import Cookies from "js-cookie";

const AgreementModal = ({ open, onClose }) => {
  const [showCheckboxes, setShowCheckboxes] = useState({ eula: false, privacy: false, saas: false });
  const [agreements, setAgreements] = useState({ eula: false, privacy: false, saas: false });
  const [activeContent, setActiveContent] = useState(null);
  const [contents, setContents] = useState({ eula: "", privacy: "", saas: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const allChecked = ["eula", "privacy", "saas"].every(
    (type) => !showCheckboxes[type] || agreements[type]
  );

  // Fetch agreement contents and accepted status
  useEffect(() => {
    const fetchContents = async () => {
      try {
        const { data } = await axios.get("/user");
        const userData = data[1].medicube_user_id;

        const [eulaResp, saasResp, privacyResp] = await Promise.all([
          axios.get("document_agreement/1", { params: { user_type: "system_user", user_id: userData } }),
          axios.get("document_agreement/2", { params: { user_type: "system_user", user_id: userData } }),
          axios.get("document_agreement/3", { params: { user_type: "system_user", user_id: userData } }),
        ]);

        setContents({
          eula: eulaResp.data.document,
          saas: saasResp.data.document,
          privacy: privacyResp.data.document,
        });

        setShowCheckboxes({
          eula: !eulaResp.data.accepted,
          saas: !saasResp.data.accepted,
          privacy: !privacyResp.data.accepted,
        });
      } catch (error) {
        console.error("Error fetching agreement contents:", error);
        setContents({
          eula: "Failed to load EULA.",
          saas: "Failed to load SaaS Agreement.",
          privacy: "Failed to load Privacy Policy.",
        });
      }
    };

    fetchContents();
  }, []);

  const handleCheckboxChange = (name) => (event) => {
    setAgreements({ ...agreements, [name]: event.target.checked });
  };

  const handleAccept = async (agreementType) => {
    try {
      const { data } = await axios.get("/user");
      const userData = data[1].medicube_user_id;

      const documentIds = { eula: 1, saas: 2, privacy: 3 };
      const legal_document_id = documentIds[agreementType];
      if (!legal_document_id) return;

      setIsSubmitting(true);

      await axios.post(
        "document_agreement",
        { legal_document_id, user_type: "system_user", user_id: userData, patient_id: 0, consent_bundle_id: 0 },
        { headers: { accept: "application/json", "Content-Type": "application/json" } }
      );

      console.log(`${agreementType} agreement accepted!`);
      setShowCheckboxes((prev) => ({ ...prev, [agreementType]: false }));
      setAgreements((prev) => ({ ...prev, [agreementType]: false }));
    } catch (error) {
      console.error("Failed to send document agreement:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMainClose = (event, reason) => {
    if (reason === "backdropClick") return;
    onClose();
  };

  const handleAcceptAll = async () => {
    setIsSubmitting(true);
    for (const key of Object.keys(agreements)) {
      if (agreements[key]) await handleAccept(key);
    }
    setIsSubmitting(false);
    onClose();
  };

  const handleLogout = () => {
    Cookies.set("token", "");
    axios.defaults.headers.common.Authorization = "";
    window.location.replace("/login");
  };

  return (
    <>
      {/* Main Agreements Modal */}
      <Modal open={open} onClose={handleMainClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
             alignItems: "center", // center horizontally
             gap: 2,
            transform: "translate(-50%, -50%)",
            width: 800,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{textAlign: "center" }}> 
            Welcome to Medicube 
          </Typography>

               <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" , mb: 3 }}>
        Our policies have been updated. Please review and accept the latest Terms of Use
          </Typography>

<Box display="flex" flexDirection="column" gap={0.5} mb={2} marginLeft={35}>
  {["eula", "privacy", "saas"].map(
    (type) =>
      showCheckboxes[type] && (
        <FormControlLabel
          key={type}
          control={
            <Checkbox
              checked={agreements[type]}
              onChange={handleCheckboxChange(type)}
            />
          }
          label={
            <span>
              I agree to the{" "}
              <Link
                component="button"
                underline="hover"
                onClick={() => setActiveContent(type)}
                sx={{ ml: 0.5 }}
              >
                {type.toUpperCase()}
              </Link>
            </span>
          }
        />
      )
  )}
</Box>


          <Box sx={{ display: "flex", gap: 2, mt: 3, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              disabled={!allChecked || isSubmitting}
              onClick={handleAcceptAll}
            >
              {isSubmitting ? "Submitting..." : "Accept All"}
            </Button>
            <Button variant="outlined" color="error" onClick={() => setShowDeclineModal(true)}>
              Decline
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Individual Agreement Content Modal */}
      <Modal open={!!activeContent} onClose={() => setActiveContent(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 1100,
            height: "70vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            overflowY: "auto",
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: activeContent ? contents[activeContent] : "" }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button variant="contained" onClick={() => setActiveContent(null)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Decline Confirmation Modal */}
      <Modal open={showDeclineModal} onClose={() => setShowDeclineModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <WarningAmber sx={{ fontSize: 40, color: "orange", mb: 2 }} />
          <Typography variant="h6">Are you sure you want to decline?</Typography>
          <Typography sx={{ mt: 2 }}>
            You will no longer be able to access Medicube services until you agree to the updated terms and conditions.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }}>
            <Button variant="contained" color="secondary" onClick={() => setShowDeclineModal(false)}>
              Go back
            </Button>
            <Button variant="contained" color="error" onClick={handleLogout}>
              Confirm & Exit
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AgreementModal;
